import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between" }
const _hoisted_2 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_3 = ["value"]
const _hoisted_4 = { class: "mb-4" }
const _hoisted_5 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_6 = { class: "block text-gray-700" }
const _hoisted_7 = { class: "mb-4" }
const _hoisted_8 = { class: "mb-4" }
const _hoisted_9 = { class: "mb-4" }
const _hoisted_10 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_11 = { class: "mb-4" }
const _hoisted_12 = { class: "mb-4" }
const _hoisted_13 = { class: "flex justify-between w-full" }
const _hoisted_14 = { class: "flex items-center gap-2" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]

import { computed, ref, Ref, watch } from "vue";
import { Tank, TankGroup, VaccinationConfiguration } from "@/types/v2";
import { useWeightForm } from "@/composables/tasks/vaccination/useWeightForm";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

import { useEquipment } from "@/composables/tasks/useEquipment";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: VaccinationConfiguration | null;
    show: boolean;
    onClose?: () => void;
}
interface GroupDropdownOption {
    key: string;
    label: string;
    rawGroup: TankGroup;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WeightModal',
  props: {
    configuration: {},
    show: { type: Boolean },
    onClose: { type: Function }
  },
  setup(__props: any) {

const props = __props;

// ===================================================================
// State
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// Composables
// ===================================================================
const { tanks, getGroupsOfTank } = useVaccination();
const { selectedTank, selectedGroup, weight, comment, resetForm, weightId } = useWeightForm();
const { createWeightRecord } = useRecord();
const {
    isIndividualWeightType,
    isNoWeightType,
    isRandomWeightType,
    shouldDisplayProgress,
    getWeighedFish,
    getWeighedFishInConfiguration,
    getTargetWeightAmount,
    getTargetWeightAmountInConfiguration
} = useWeight();

const {
    hasWeightIdRequirement,
    getWeightId
} = useEquipment();

// ===================================================================
// Computed
// ===================================================================
const availableGroups = computed<TankGroup[]>(() => {
    if (!selectedTank.value) return [];
    return getGroupsOfTank(selectedTank.value.key)
});

const getGroupTagCombination = (group: TankGroup | null | undefined): string => {
    if (!group?.tags || group.tags.length === 0) return 'Unmarked';

    const tagTitles = group.tags
        .filter(tag => tag.title !== 'Start Tag')
        .map(tag => tag.title);

    return tagTitles.length ? tagTitles.join(' + ') : 'Unmarked';
};

const availableGroupOptions = computed<GroupDropdownOption[]>(() => {
    return availableGroups.value.map(group => ({
        key: group.key,
        label: getGroupTagCombination(group),
        rawGroup: group
    }));
});

const selectedGroupOption = computed<{ key: string; label: string } | null>(() => {
    if (!selectedGroup.value) return null;

    return {
        key: selectedGroup.value.key,
        label: getGroupTagCombination(selectedGroup.value)
    };
});

const resolveConfigurationTank = (
    configuration: VaccinationConfiguration | null
): Tank | null => {
    if (!configuration) return null;
    if ('destinationTank' in configuration && configuration.destinationTank) {
        return configuration.destinationTank;
    }
    if ('tank' in configuration && configuration.tank) {
        return configuration.tank;
    }
    return null;
};

const resolveConfigurationGroup = (
    configuration: VaccinationConfiguration | null
): TankGroup | null => {
    if (!configuration) return null;
    if ('destinationGroup' in configuration && configuration.destinationGroup) {
        return configuration.destinationGroup;
    }
    return null;
};

const isTankLocked = computed(() => !!resolveConfigurationTank(props.configuration));
const isGroupLocked = computed(() => !!resolveConfigurationGroup(props.configuration));

// ===================================================================
// Actions
// ===================================================================
// Modal
const closeModal = () => {
    if (props.onClose) props.onClose();
};

// Handle Record Weight
const recordWeight = (keepModalOpen = false): void => {
    // Get current weight_id from equipment configuration (in case it was updated)
    const currentWeightId = hasWeightIdRequirement() ? getWeightId() : null;
    
    createWeightRecord({
        weightId: currentWeightId || weightId.value || null,
        configurationKey: props.configuration?.key ?? null,
        tank: selectedTank.value ?? null,
        group: selectedGroup.value ?? null,
        comment: comment.value,
        weight: weight.value,
        timestamp: new Date().toISOString()
    });

    resetForm();
    
    // Reload weight_id after reset if requirement exists
    if (hasWeightIdRequirement()) {
        const equipmentWeightId = getWeightId();
        if (equipmentWeightId) {
            weightId.value = equipmentWeightId;
        }
    }

    if (!keepModalOpen) {
        return closeModal();
    }
};

// Event handlers for ItemDropdown
const handleTankChange = (event: { item: Tank }) => {
    selectedTank.value = event.item;
    selectedGroup.value = null;
};

const handleGroupChange = (event: { item: GroupDropdownOption }) => {
    selectedGroup.value = event.item?.rawGroup ?? null;
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
        // Load weight_id from equipment configuration when modal opens
        if (newVal && hasWeightIdRequirement()) {
            const equipmentWeightId = getWeightId();
            if (equipmentWeightId) {
                weightId.value = equipmentWeightId;
            }
        }
    }
);

watch(
    () => props.configuration,
    (newConfiguration) => {
        selectedTank.value = resolveConfigurationTank(newConfiguration);
        selectedGroup.value = resolveConfigurationGroup(newConfiguration);
    },
    { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_ItemDropdown = _resolveComponent("ItemDropdown")!

  return (showModal.value)
    ? (_openBlock(), _createBlock(ModalBox, {
        key: 0,
        onClose: closeModal
      }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _cache[7] || (_cache[7] = _createElementVNode("span", null, "Record Weight  ", -1)),
            _createElementVNode("span", null, [
              (_unref(shouldDisplayProgress))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[6] || (_cache[6] = _createTextVNode(" | Progress: ")),
                    (_unref(isIndividualWeightType))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(_ctx.configuration ? _unref(getWeighedFishInConfiguration)(_ctx.configuration) : 0) + " / " + _toDisplayString(_ctx.configuration ? _unref(getTargetWeightAmountInConfiguration)(_ctx.configuration) : 0), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (_unref(isRandomWeightType))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(_toDisplayString(_unref(getWeighedFish)) + " / " + _toDisplayString(_unref(getTargetWeightAmount)), 1)
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        main: _withCtx(() => [
          (_unref(hasWeightIdRequirement)())
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-gray-700" }, " Weight ID ", -1)),
                _createElementVNode("input", {
                  type: "text",
                  value: _unref(weightId) || '',
                  disabled: "",
                  class: "form-input p-2 mt-1 block w-full border border-gray-300 rounded bg-gray-100 cursor-not-allowed opacity-75",
                  placeholder: "Set in Equipment page"
                }, null, 8, _hoisted_3),
                _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-xs text-gray-500 mt-1" }, "Configure this value in the Equipment page", -1))
              ]))
            : _createCommentVNode("", true),
          (_unref(isNoWeightType) || _unref(isRandomWeightType))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("div", _hoisted_4, [
                  _cache[10] || (_cache[10] = _createElementVNode("label", { class: "block text-gray-700" }, "Tank (Optional):", -1)),
                  _createVNode(_component_ItemDropdown, {
                    index: -1,
                    label: '',
                    items: _unref(tanks),
                    itemKey: 'tank',
                    neutralStyle: true,
                    selectedItem: _unref(selectedTank) ?? {},
                    onFieldChange: handleTankChange
                  }, null, 8, ["items", "selectedItem"])
                ]),
                (_unref(selectedTank))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("label", _hoisted_6, " Animal Group " + _toDisplayString(_unref(isIndividualWeightType) ? "(Required)" : "(Optional)") + ": ", 1),
                      _createVNode(_component_ItemDropdown, {
                        index: -1,
                        label: '',
                        items: availableGroupOptions.value,
                        itemKey: 'tag',
                        neutralStyle: true,
                        selectedItem: selectedGroupOption.value ?? {},
                        onFieldChange: handleGroupChange
                      }, null, 8, ["items", "selectedItem"])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_7, [
                  _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-gray-700" }, "Weight (Grams):", -1)),
                  _withDirectives(_createElementVNode("input", {
                    type: "number",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(weight) ? (weight).value = $event : null)),
                    class: "form-input p-2 mt-1 block w-full border border-gray-300 rounded"
                  }, null, 512), [
                    [_vModelText, _unref(weight)]
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _cache[12] || (_cache[12] = _createElementVNode("label", { class: "block text-gray-700" }, "Comment:", -1)),
                  _withDirectives(_createElementVNode("textarea", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(comment) ? (comment).value = $event : null)),
                    class: "form-input p-2 mt-1 block w-full border border-gray-300 rounded",
                    placeholder: "Leave a comment"
                  }, null, 512), [
                    [_vModelText, _unref(comment)]
                  ])
                ])
              ], 64))
            : (_unref(isIndividualWeightType))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createElementVNode("div", _hoisted_9, [
                    _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-gray-700" }, "Tank (Required):", -1)),
                    _createVNode(_component_ItemDropdown, {
                      index: -1,
                      label: '',
                      disabled: isTankLocked.value,
                      items: _unref(tanks),
                      itemKey: 'tank',
                      neutralStyle: true,
                      selectedItem: _unref(selectedTank) ?? {},
                      onFieldChange: handleTankChange
                    }, null, 8, ["disabled", "items", "selectedItem"])
                  ]),
                  (_unref(selectedTank))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-gray-700" }, "Animal Group (Required):", -1)),
                        _createVNode(_component_ItemDropdown, {
                          index: -1,
                          label: '',
                          disabled: isGroupLocked.value,
                          items: availableGroupOptions.value,
                          itemKey: 'tag',
                          neutralStyle: true,
                          selectedItem: selectedGroupOption.value || {},
                          onFieldChange: handleGroupChange
                        }, null, 8, ["disabled", "items", "selectedItem"])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_11, [
                    _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-gray-700" }, "Weight (Grams):", -1)),
                    _withDirectives(_createElementVNode("input", {
                      type: "number",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(weight) ? (weight).value = $event : null)),
                      class: "form-input p-2 mt-1 block w-full border border-gray-300 rounded"
                    }, null, 512), [
                      [_vModelText, _unref(weight)]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _cache[16] || (_cache[16] = _createElementVNode("label", { class: "block text-gray-700" }, "Comment:", -1)),
                    _withDirectives(_createElementVNode("textarea", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(comment) ? (comment).value = $event : null)),
                      class: "form-input p-2 mt-1 block w-full border border-gray-300 rounded",
                      placeholder: "Leave a comment"
                    }, null, 512), [
                      [_vModelText, _unref(comment)]
                    ])
                  ])
                ], 64))
              : _createCommentVNode("", true)
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("button", {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (recordWeight(false))),
                type: "button",
                disabled: !_unref(weight),
                class: "disabled:opacity-25 text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              }, " Save and close ", 8, _hoisted_15),
              _createElementVNode("button", {
                onClick: _cache[5] || (_cache[5] = ($event: any) => (recordWeight(true))),
                type: "button",
                disabled: !_unref(weight),
                class: "disabled:opacity-25 text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              }, "Save ", 8, _hoisted_16)
            ]),
            _createElementVNode("button", {
              onClick: closeModal,
              type: "button",
              class: "text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
            }, " Back ")
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})